import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Add request/response interceptors for debugging
api.interceptors.request.use(
  config => {
    console.log('API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  error => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => {
    console.log('API Response:', response.config.url, response.status);
    return response;
  },
  error => {
    console.error('API Response Error:', error.config?.url, error.message);
    return Promise.reject(error);
  }
);

// Health check
export const getHealth = () => api.get('/health');

// Task Management (updated from Queue Management)
export const getTaskStatus = (n8nWorkflowId?: string) =>
  api.get('/tasks/status', { params: { n8nWorkflowId } });

export const getAllTasks = (params?: { status?: string; n8nWorkflowId?: string; priority?: string }) =>
  api.get('/tasks', { params });

export const getMyTasks = (userId: string) =>
  api.get('/tasks/my', { params: { userId } });

export const getTask = (taskId: string) =>
  api.get(`/tasks/${taskId}`);

export const claimTask = (userId: string, formTemplateIds?: string[]) =>
  api.post('/tasks/claim', { userId, formTemplateIds });

export const claimSpecificTask = (taskId: string, userId: string) =>
  api.post(`/tasks/${taskId}/claim`, { userId });

export const releaseTask = (taskId: string, userId: string) =>
  api.post(`/tasks/${taskId}/release`, { userId });

export const saveDraft = (taskId: string, data: any) =>
  api.patch(`/tasks/${taskId}/save-draft`, { data });

export const startTask = (taskId: string, userId: string) =>
  api.post(`/tasks/${taskId}/start`, { userId });

export const overrideTask = (taskId: string, managerId: string, newUserId: string) =>
  api.post(`/tasks/${taskId}/override`, { managerId, newUserId });

export const deleteTask = (taskId: string) =>
  api.delete(`/tasks/${taskId}`);

export const deleteTasks = (taskIds: string[]) =>
  api.post('/tasks/bulk-delete', { taskIds });

// Backward compatibility aliases
export const getQueueStatus = (workflowType?: string) => getTaskStatus(workflowType);
export const getUserTasks = (userId: string) => getMyTasks(userId);

// Forms
export const getFormTemplates = (params?: any) =>
  api.get('/forms/templates', { params });

export const createFormTemplate = (template: any) =>
  api.post('/forms/templates', template);

export const updateFormTemplate = (id: string, template: any) =>
  api.put(`/forms/templates/${id}`, template);

export const publishFormTemplate = (id: string) =>
  api.post(`/forms/templates/${id}/publish`);

export const archiveFormTemplate = (id: string) =>
  api.post(`/forms/templates/${id}/archive`);

export const getFormTemplate = (id: string) =>
  api.get(`/forms/templates/${id}`);

// Form Submissions
export const getFormSubmissions = (params?: any) =>
  api.get('/forms/submissions', { params });

export const submitForm = (taskId: string, formTemplateId: string, data: any, submittedBy?: string) =>
  api.post('/forms/submit', { taskId, formTemplateId, data, submittedBy });

export const getFormSubmission = (id: string) =>
  api.get(`/forms/submissions/${id}`);

export const getFormStatistics = (formTemplateId?: string) =>
  api.get('/forms/statistics', { params: { formTemplateId } });

// Webhooks (n8n integration)
export const createTaskViaWebhook = (data: {
  n8nExecutionId: string;
  n8nWorkflowId: string;
  formTemplateId: string;
  taskData: any;
  priority?: string;
  resumeWebhookUrl: string;
}) => api.post('/webhook/task/create', data);

export const getWebhookHealth = () => api.get('/webhook/health');

// Deprecated/Removed endpoints (for reference - will error if called)
// These are removed from the backend:
// - /workflows/templates (WorkflowTemplates - now managed in n8n)
// - /polling/* (Polling - now done in n8n)

export default api;
